import { useAuth } from '@contexts/AuthContext';
import type { AnalyticsFilter } from '@parlay-picker/shared/analyticsScopes';
import { type ApiErrorResponse, safeParseJson } from '@utils/api';
import { useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface Bet {
  id: number;
  name?: string;
  league?: string;
  stake?: number | string;
  odds?: string;
  totalOdds?: string;
  result?: string;
  returned?: number;
  actualPayout?: number | string;
  gameDate?: string;
  createdAt?: string;
  settledAt?: string;
  legs?: number;
  legCount?: number;
  [key: string]: unknown;
}

export interface UseBetsResult {
  bets: Bet[];
  loading: boolean;
  error: string | null;
}

// =============================================================================
// useBets Hook
// =============================================================================

export function useBets(filter: AnalyticsFilter = 'all_tracked'): UseBetsResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [bets, setBets] = useState<Bet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadBets() {
      setLoading(true);
      setError(null);

      if (isAuthenticated) {
        try {
          // "real_bets" = only sportsbook-imported bets (origin='bet')
          // "all_tracked" / "everything" = all user parlays regardless of origin
          const url = filter === 'real_bets' ? '/api/bets' : '/api/parlays?results=won,lost';
          const res = await authFetch(url);
          if (res.ok) {
            const data = await safeParseJson<{ bets?: Bet[]; parlays?: Bet[]; error?: string }>(
              res,
            );
            if ((data as ApiErrorResponse).error) throw new Error((data as ApiErrorResponse).error);
            const responseData = data as { bets?: Bet[]; parlays?: Bet[] };
            setBets(responseData.bets || responseData.parlays || []);
          } else {
            setBets([]);
          }
        } catch (_err) {
          setBets([]);
        }
      } else {
        // Not authenticated - no bets to show
        setBets([]);
      }

      setLoading(false);
    }

    loadBets();
  }, [isAuthenticated, authFetch, filter]);

  return {
    bets,
    loading,
    error,
  };
}
